<template>
  <div class="conversation-list admin-dashboard-modern">
    <!-- Page Header with Icon -->
    <div class="page-header">
      <div class="page-header-content">
        <div class="page-icon">
          <i class="fa fa-comments"></i>
        </div>
        <div class="page-header-text">
          <h2 class="page-title">Bot Conversations</h2>
          <p class="page-subtitle">View and manage all bot conversation logs</p>
        </div>
      </div>
    </div>

    <!-- Filter Controls - Panel Card style with header -->
    <div class="panel-card filter-panel">
      <div class="panel-header">
        <h3 class="panel-title">
          <i class="fa fa-filter panel-icon"></i>
          Filter Conversations
        </h3>
      </div>
      <div class="panel-body filter-body">
        <div class="filter-grid">
          <div class="filter-group">
            <label class="filter-label">Needs Attention</label>
            <div class="toggle-group">
              <button
                @click="activeFilter.needsAttention = false"
                :class="['toggle-btn', { active: !activeFilter.needsAttention }]"
              >
                <i class="fa fa-list-ul"></i>
                All Calls
              </button>
              <button
                @click="activeFilter.needsAttention = true"
                :class="['toggle-btn danger', { active: activeFilter.needsAttention }]"
              >
                <i class="fa fa-exclamation-triangle"></i>
                Needs Attention
              </button>
            </div>
          </div>

          <div class="filter-group">
            <label class="filter-label">Sentiment</label>
            <select v-model="activeFilter.sentiment" class="form-select">
              <option value="all">All Sentiment</option>
              <option value="negative">Negative</option>
              <option value="neutral">Neutral</option>
              <option value="positive">Positive</option>
            </select>
          </div>

          <div class="filter-group">
            <label class="filter-label">Outcome</label>
            <select v-model="activeFilter.outcome" class="form-select">
              <option value="all">All Outcomes</option>
              <option value="booked">Booked</option>
              <option value="rescheduled">Rescheduled</option>
              <option value="cancelled">Cancelled</option>
              <option value="declined">Declined</option>
              <option value="callback_requested">Callback requested</option>
              <option value="info_provided">Info provided</option>
              <option value="transferred">Transferred</option>
              <option value="do_not_contact">Do not contact</option>
              <option value="voicemail">Voicemail</option>
              <option value="voicemail_full">Voicemail (full)</option>
              <option value="no_answer">No answer</option>
              <option value="failed">Failed</option>
              <option value="abandoned">Abandoned</option>
            </select>
          </div>

          <div class="filter-group">
            <label class="filter-label">Mode</label>
            <div class="toggle-group">
              <button
                @click="activeFilter.mode = 'all'"
                :class="['toggle-btn', { active: activeFilter.mode === 'all' }]"
              >
                All
              </button>
              <button
                @click="activeFilter.mode = 'inbound'"
                :class="['toggle-btn', { active: activeFilter.mode === 'inbound' }]"
              >
                <i class="fa fa-arrow-down"></i>
                Inbound
              </button>
              <button
                @click="activeFilter.mode = 'outbound'"
                :class="['toggle-btn', { active: activeFilter.mode === 'outbound' }]"
              >
                <i class="fa fa-arrow-up"></i>
                Outbound
              </button>
            </div>
          </div>

          <div class="filter-group">
            <label class="filter-label">Date Range</label>
            <select v-model="activeFilter.dateRange" @change="handleDateRangeChange" class="form-select">
              <option :value="null">Select Date Range</option>
              <option value="today">Today</option>
              <option value="yesterday">Yesterday</option>
              <option value="week">Last 7 Days</option>
              <option value="month">Last 30 Days</option>
              <option value="custom">Custom Range</option>
            </select>
          </div>

          <!-- Custom Date Range Pickers -->
          <template v-if="activeFilter.dateRange === 'custom'">
            <div class="filter-group">
              <label class="filter-label" for="customStartDate">Start Date</label>
              <input type="date" id="customStartDate" v-model="activeFilter.customStartDate" class="form-select custom-date-input">
            </div>
            <div class="filter-group">
              <label class="filter-label" for="customEndDate">End Date</label>
              <input type="date" id="customEndDate" v-model="activeFilter.customEndDate" class="form-select custom-date-input">
            </div>
          </template>
        </div>

        <div class="filter-actions">
          <button class="btn reset-btn" @click="resetFilters">
            <i class="fa fa-undo"></i>
            Reset
          </button>
          <button class="btn apply-btn" @click="applyFilters">
            <i class="fa fa-search"></i>
            Apply Filters
          </button>
        </div>
      </div>
    </div>
    
    <!-- Conversations Table - Panel Card style -->
    <div class="panel-card">
      <div class="panel-header">
        <h3 class="panel-title">
          <i class="fa fa-comments panel-icon"></i>
          Conversations
        </h3>
        <span class="result-count" v-if="calls && calls.length > 0">
          {{ calls.length }} result{{ calls.length !== 1 ? 's' : '' }}
        </span>
      </div>
      <div class="panel-body table-panel">
        <div class="table-header">
          <div class="header-cell cell-attn"></div>
          <div class="header-cell cell-date">Date/Time</div>
          <div class="header-cell cell-mode">Mode</div>
          <div class="header-cell cell-outcome">Outcome</div>
          <div class="header-cell cell-patient">Patient</div>
          <div class="header-cell cell-sentiment">Sentiment</div>
          <div class="header-cell cell-cs">CS Score</div>
          <div class="header-cell cell-duration">Duration</div>
          <div class="header-cell cell-cost">Cost*</div>
          <div class="header-cell cell-actions">Actions</div>
        </div>

      <div v-if="isFetchingList && (siteCallsCurrentPageNumber <= 1)" class="loading-state">
        <div class="loading-spinner"></div>
        <div class="loading-text">Loading calls...</div>
      </div>

      <div v-else-if="vuexError && (!calls || calls.length === 0)" class="error-state">
        <div class="error-icon">⚠️</div>
        <div class="error-text">Error loading calls:</div>
        <div class="error-message">{{ vuexError }}</div>
        <button @click="applyFilters" class="btn retry-btn">Try Again</button>
      </div>

      <div v-else-if="!calls || calls.length === 0" class="empty-state">
        <div class="empty-icon">📭</div>
        <div class="empty-text">No calls found</div>
        <div class="empty-subtext">Try adjusting your filters or check back after the next call.</div>
      </div>

      <template v-else>
        <!-- data-label on each cell drives the stacked-card layout on mobile, where the
             header row is hidden and each cell shows its column name via ::before. -->
        <div
          v-for="call in calls"
          :key="call.id"
          class="table-row"
          :class="{ 'row-attn': call.needs_attention }"
          @click="viewConversation(call)"
        >
          <div class="table-cell cell-attn" data-label="">
            <span
              v-if="call.needs_attention"
              class="attn-marker"
              :title="call.reason || 'Needs attention'"
            >⚠️</span>
          </div>
          <div class="table-cell cell-date" data-label="Date/Time">{{ formatStartedAt(call.started_at) }}</div>
          <div class="table-cell cell-mode" data-label="Mode">
            <span class="mode-label">{{ formatMode(call.mode) }}</span>
          </div>
          <div class="table-cell cell-outcome" data-label="Outcome">
            <span class="status-badge" :class="outcomeBadgeClass(call.outcome)">{{ formatOutcome(call.outcome) }}</span>
          </div>
          <div class="table-cell cell-patient" data-label="Patient">{{ call.patient_id || '—' }}</div>
          <div class="table-cell cell-sentiment" data-label="Sentiment">
            <span
              v-if="call.patient_sentiment"
              class="sentiment-chip"
              :class="'sentiment-' + call.patient_sentiment"
            >{{ formatSentiment(call.patient_sentiment) }}</span>
            <span v-else class="sentiment-chip sentiment-unknown">—</span>
          </div>
          <div class="table-cell cell-cs" data-label="CS Score">{{ call.cs_score !== null && call.cs_score !== undefined ? call.cs_score : '—' }}</div>
          <div class="table-cell cell-duration" data-label="Duration">{{ formatDuration(call.duration_seconds) }}</div>
          <div class="table-cell cell-cost" data-label="Cost*" :title="'Internal cost — not shown to clients'">{{ formatCost(call.elevenlabs_cost_cents) }}</div>
          <div class="table-cell cell-actions" data-label="">
            <button class="action-btn view-btn" @click.stop="viewConversation(call)">
              View
            </button>
            <button v-if="call.elevenlabs_conversation_id" class="action-btn audio-btn" @click.stop="playAudio(call)">
              🔊
            </button>
          </div>
        </div>
      </template>
      </div>
    </div>

    <!-- Pagination -->
    <div class="pagination-controls" v-if="calls && calls.length > 0">
      <div class="page-info">
        <span>Page {{ siteCallsCurrentPageNumber }}</span>
        <span v-if="isFetchingList"> - Loading more...</span>
      </div>
      <div class="page-buttons">
        <button
          class="page-btn next-btn"
          :disabled="!siteCallsNextCursor || isFetchingList"
          @click="loadNextPage"
        >
          Next
        </button>
      </div>
    </div>
    <p class="cost-footnote" v-if="calls && calls.length > 0">* Cost is an internal estimate and is not visible to clients.</p>

    <!-- Audio Player Modal -->
    <div v-if="showAudioPlayerModal" class="audio-player-modal">
      <div class="modal-backdrop" @click="closeAudioPlayer"></div>
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h3 class="modal-title">Play Conversation Audio</h3>
            <button class="modal-close" @click="closeAudioPlayer">&times;</button>
          </div>
          <div class="modal-body">
            <div v-if="isLoadingAudio" class="loading-indicator">
              <div class="spinner"></div> Loading audio...
            </div>
            <div v-else-if="audioError" class="error-message">
              <p>Could not load audio: {{ audioError }}</p>
              <button @click="retryPlayAudio">Retry</button>
            </div>
            <audio v-else-if="currentAudioSource" :key="audioPlayerKey" :src="currentAudioSource" controls autoplay>
              Your browser does not support the audio element.
            </audio>
            <div v-else>
              No audio source available.
            </div>
          </div>
          <div class="modal-footer">
            <button class="btn btn-secondary" @click="closeAudioPlayer">Close</button>
          </div>
        </div>
      </div>
    </div>

    <!-- ADD THE SLIDEOUT PANEL TAG HERE -->
    <slideout-panel></slideout-panel> 

  </div>
</template>

<script>
import { mapState, mapGetters, mapActions, mapMutations } from 'vuex';
import moment from 'moment';
import Multiselect from 'vue-multiselect';
import DatePicker from 'vue2-datepicker';
import CommonOverlay from '@/components/CommonOverlay.vue';
import mixin from '../../../../mixin.js';
import ConversationDetail from './ConversationDetail.vue';

export default {
  name: 'ConversationList',
  mixins: [mixin],
  components: {
    Multiselect,
    DatePicker,
    CommonOverlay,
    ConversationDetail
  },
  data() {
    return {
      activeFilter: {
        needsAttention: false,
        sentiment: 'all',
        outcome: 'all',
        mode: 'all',
        dateRange: null,
        pageSize: 20,
        customStartDate: null,
        customEndDate: null,
      },
      showAudioPlayerModal: false,
      audioPlayerKey: 0,
      lastPlayedConversationId: null,
    };
  },
  computed: {
    ...mapState('botAdmin', {
      calls: state => state.siteCalls,
      isFetchingList: state => state.isLoadingSiteCalls,
      vuexError: state => state.siteCallsError,
      siteCallsNextCursor: state => state.siteCallsNextCursor,
      siteCallsCurrentPageNumber: state => state.siteCallsCurrentPageNumber,
      elevenLabsAudioSrcFromStore: state => state.elevenLabsAudioSrc,
      isLoadingAudio: state => state.elevenLabsLoadingAudio,
      audioError: state => state.elevenLabsAudioError,
    }),
    currentAudioSource() {
      return this.elevenLabsAudioSrcFromStore;
    },
    siteTimezone() {
      const tz = this.allSettings?.GENERAL_TIMEZONE?.PropertyValue;
      return tz ? tz.toUpperCase() : moment.tz.guess();
    }
  },
  methods: {
    ...mapActions('botAdmin', [
      'fetchSiteCallHistory',
      'fetchElevenLabsAudio'
    ]),
    ...mapMutations('botAdmin', [
        'CLEAR_ELEVENLABS_AUDIO_DETAIL',
        'SET_SITE_CALLS_LOADING',
        'SET_ELEVENLABS_LOADING_DETAIL',
        'SET_ELEVENLABS_LOADING_AUDIO'
    ]),
    formatStartedAt(startedAt) {
      if (!startedAt) return 'N/A';
      // started_at is an ISO/SQL datetime (UTC); convert to site timezone for display.
      const m = moment.utc(startedAt);
      if (!m.isValid()) return 'N/A';
      return m.tz(this.siteTimezone).format('YYYY-MM-DD hh:mm A');
    },
    formatDuration(seconds) {
      if (seconds === null || seconds === undefined) return 'N/A';
      const duration = moment.duration(seconds, 'seconds');
      const h = String(duration.hours()).padStart(1, '0');
      const m = String(duration.minutes()).padStart(2, '0');
      const s = String(duration.seconds()).padStart(2, '0');
      if (duration.hours() > 0) {
        return `${h}:${m}:${s}`;
      }
      return `${m}:${s}`;
    },
    formatMode(mode) {
      if (!mode) return '—';
      return mode.charAt(0).toUpperCase() + mode.slice(1);
    },
    formatOutcome(outcome) {
      if (!outcome) return '—';
      return String(outcome)
        .split('_')
        .map(w => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ');
    },
    outcomeBadgeClass(outcome) {
      switch (outcome) {
        case 'booked':
          return 'completed';
        case 'rescheduled':
          return 'in-progress';
        case 'cancelled':
        case 'transferred':
          return 'failed';
        default:
          return 'unknown';
      }
    },
    formatSentiment(sentiment) {
      if (!sentiment) return '—';
      return sentiment.charAt(0).toUpperCase() + sentiment.slice(1);
    },
    formatCost(cents) {
      if (cents === null || cents === undefined) return '—';
      return '$' + (cents / 100).toFixed(2);
    },
    handleDateRangeChange() {
      if (this.activeFilter.dateRange !== 'custom') {
        // Reset custom dates when not using custom range
        this.activeFilter.customStartDate = null;
        this.activeFilter.customEndDate = null;
      } else {
        if (!this.activeFilter.customStartDate) {
          this.activeFilter.customStartDate = moment().tz(this.siteTimezone).subtract(7, 'days').format('YYYY-MM-DD');
        }
        if (!this.activeFilter.customEndDate) {
          this.activeFilter.customEndDate = moment().tz(this.siteTimezone).format('YYYY-MM-DD');
        }
      }
    },
    // Translate the active date-range filter into { from, to } ISO datetimes
    // (in the site timezone) for the call-history endpoint. Returns {} when no
    // date range is selected.
    buildDateFilters() {
      let startDate = null;
      let endDate = null;
      const nowInSiteTimezone = moment().tz(this.siteTimezone);

      switch (this.activeFilter.dateRange) {
        case 'today':
          startDate = nowInSiteTimezone.clone().startOf('day');
          endDate = nowInSiteTimezone.clone().endOf('day');
          break;
        case 'yesterday':
          startDate = nowInSiteTimezone.clone().subtract(1, 'days').startOf('day');
          endDate = nowInSiteTimezone.clone().subtract(1, 'days').endOf('day');
          break;
        case 'week':
          startDate = nowInSiteTimezone.clone().subtract(7, 'days').startOf('day');
          endDate = nowInSiteTimezone.clone().endOf('day');
          break;
        case 'month':
          startDate = nowInSiteTimezone.clone().subtract(30, 'days').startOf('day');
          endDate = nowInSiteTimezone.clone().endOf('day');
          break;
        case 'custom':
          if (this.activeFilter.customStartDate) {
            startDate = moment.tz(this.activeFilter.customStartDate, this.siteTimezone).startOf('day');
          }
          if (this.activeFilter.customEndDate) {
            endDate = moment.tz(this.activeFilter.customEndDate, this.siteTimezone).endOf('day');
          } else if (startDate && startDate.isValid()) {
            endDate = nowInSiteTimezone.clone().endOf('day');
          }
          break;
      }

      const out = {};
      if (startDate && startDate.isValid()) out.from = startDate.toISOString();
      if (endDate && endDate.isValid()) out.to = endDate.toISOString();
      return out;
    },
    buildFilters() {
      const dateFilters = this.buildDateFilters();
      return {
        needsAttention: this.activeFilter.needsAttention,
        sentiment: this.activeFilter.sentiment,
        outcome: this.activeFilter.outcome,
        mode: this.activeFilter.mode,
        ...dateFilters,
      };
    },
    async loadCalls({ cursor = null, newSearchOrFilter = false } = {}) {
      if (!this.siteID) {
        this.$toast.error('No active site selected. Cannot fetch calls.');
        return;
      }

      await this.fetchSiteCallHistory({
        siteId: this.siteID,
        filters: this.buildFilters(),
        cursor,
        limit: this.activeFilter.pageSize,
        newSearchOrFilter,
      });
    },
    applyFilters() {
      this.loadCalls({ newSearchOrFilter: true });
    },
    resetFilters() {
      this.activeFilter = {
        needsAttention: false,
        sentiment: 'all',
        outcome: 'all',
        mode: 'all',
        dateRange: null,
        pageSize: this.activeFilter.pageSize,
        customStartDate: null,
        customEndDate: null,
      };
      this.loadCalls({ newSearchOrFilter: true });
    },
    viewConversation(call) {
      // Only ElevenLabs-backed calls have a transcript/audio to view.
      if (!call.elevenlabs_conversation_id) {
        this.$toast.info('No transcript available for this call yet.');
        return;
      }
      this.SET_ELEVENLABS_LOADING_DETAIL(true);
      this.SET_ELEVENLABS_LOADING_AUDIO(true);

      this.$showPanel({
        component: ConversationDetail,
        openOn: 'right',
        props: {
          conversationId: call.elevenlabs_conversation_id,
          siteId: this.siteID,
          // Thread the analysis fields the list already has on the call row so
          // the detail's Sentiment / Call Quality panel renders without an
          // extra backend call. (Explicit `|| null` rather than `??` — this
          // file's ESLint code-path analyzer crashes on nullish-coalescing.)
          patientSentiment: call.patient_sentiment || null,
          csScore: (call.cs_score === null || call.cs_score === undefined) ? null : call.cs_score,
          needsAttentionFlag: call.needs_attention || null,
          reason: call.reason || null,
          outcome: call.outcome || null,
          patientId: (call.patient_id === null || call.patient_id === undefined) ? null : call.patient_id,
        },
        width: 800,
      });
    },
    async playAudio(call) {
      if (!call.elevenlabs_conversation_id) return;
      this.lastPlayedConversationId = call.elevenlabs_conversation_id;
      this.audioPlayerKey += 1;
      this.showAudioPlayerModal = true;
      await this.fetchElevenLabsAudio({ conversationId: call.elevenlabs_conversation_id, siteID: this.siteID });
    },
    closeAudioPlayer() {
      this.showAudioPlayerModal = false;
      this.CLEAR_ELEVENLABS_AUDIO_DETAIL();
      this.lastPlayedConversationId = null;
      const audioElement = this.$el.querySelector('.audio-player-modal audio');
      if (audioElement) {
        audioElement.pause();
        audioElement.src = '';
      }
    },
    async retryPlayAudio() {
      if (this.lastPlayedConversationId) {
        this.audioPlayerKey += 1;
        await this.fetchElevenLabsAudio({ conversationId: this.lastPlayedConversationId, siteID: this.siteID });
      }
    },
    loadNextPage() {
      if (this.siteCallsNextCursor && !this.isFetchingList) {
        this.loadCalls({ cursor: this.siteCallsNextCursor, newSearchOrFilter: false });
      }
    },
  },
  mounted() {
    if (this.siteID) {
      this.loadCalls({ newSearchOrFilter: true });
    }
  },
  watch: {
    siteID: {
      handler(newVal, oldVal) {
        if (newVal && newVal !== oldVal) {
          this.loadCalls({ newSearchOrFilter: true });
        }
      },
    },
  }
};
</script>

<style lang="scss" scoped>
@import '../../AppointmentCalendarModern.scss';

.conversation-list {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 1rem; /* Add padding so content doesn't touch container edges */
  font-family: var(--aid-font);
}

/* Page Header - Enhanced with icon */
.page-header {
  margin-bottom: 0;
}

.page-header-content {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.page-icon {
  width: 44px;
  height: 44px;
  background: var(--aid-accent-soft);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: none;

  i {
    color: var(--aid-accent);
    font-size: 1.15rem;
  }
}

.page-header-text {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.page-title {
  font-size: 18px;
  margin: 0;
  font-weight: 600;
  color: var(--aid-text);
  letter-spacing: -0.1px;
}

.page-subtitle {
  margin: 0;
  font-size: 13px;
  color: var(--aid-text-mute);
  font-weight: 400;
}

/* Panel Card Pattern */
.panel-card {
  background: var(--aid-surface);
  border-radius: 10px;
  border: 1px solid var(--aid-border);
  box-shadow: var(--aid-shadow-card);
  overflow: hidden;
}

.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid var(--aid-border);
  background: var(--aid-surface2);
}

.panel-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 14px;
  font-weight: 600;
  color: var(--aid-text);
  margin: 0;
}

.panel-icon {
  color: var(--aid-accent);
  font-size: 0.875rem;
}

.panel-body {
  padding: 1.25rem;
}

.panel-body.table-panel {
  padding: 0;
}

/* Tablet (769–991px): the 10-column flex table is wider than the content area once
   the rail takes its share, so let the table scroll horizontally inside the panel
   instead of overflowing the card. Below 768px it switches to stacked cards (no
   scroll needed), so this only applies in the tablet band. The header + rows share a
   min-width so their columns stay aligned while scrolling. */
@media (min-width: 769px) and (max-width: 991px) {
  .panel-body.table-panel {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
  .table-header,
  .table-row {
    min-width: 920px;
  }
  .panel-body.table-panel::-webkit-scrollbar {
    height: 6px;
  }
  .panel-body.table-panel::-webkit-scrollbar-thumb {
    background: var(--aid-border);
    border-radius: 4px;
  }
}

.result-count {
  font-size: 12px;
  font-weight: 500;
  color: var(--aid-text-mute);
  background: var(--aid-surface2);
  padding: 0.2rem 0.7rem;
  border-radius: 999px;
}

/* Filter Panel - Redesigned */
.filter-panel {
  padding: 0;
}

.filter-body {
  padding: 1.25rem;
}

.filter-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.25rem;
  margin-bottom: 1.25rem;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.filter-label {
  font-size: 11px;
  font-weight: 600;
  color: var(--aid-text-mute);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Toggle Button Group — TxPresenter segmented control (.txb-view-switcher):
   surface2 track, transparent tabs, active tab = white pill w/ teal ink + soft
   shadow. */
.toggle-group {
  display: flex;
  background: var(--aid-surface2);
  border-radius: 8px;
  padding: 3px;
  gap: 2px;
}

.toggle-btn {
  flex: 1;
  padding: 0.4rem 0.7rem;
  background: transparent;
  border: none;
  border-radius: 6px;
  color: var(--aid-text-mute);
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
  font-family: var(--aid-font);
  white-space: nowrap;

  i {
    font-size: 0.75rem;
  }

  &:hover:not(.active) {
    color: var(--aid-text);
  }

  &.active {
    background: var(--aid-surface);
    color: var(--aid-accent-ink);
    box-shadow: var(--aid-shadow-sm);
    font-weight: 600;
  }

  &.success.active {
    color: var(--aid-success);
  }

  &.danger.active {
    color: var(--aid-danger-text);
  }
}

/* Select — TxPresenter look: 6px radius, soft border, our chevron, teal focus ring. */
.form-select {
  padding: 7px 30px 7px 10px;
  font-size: 13px;
  line-height: 1.3;
  border: 1px solid var(--aid-border);
  border-radius: 6px;
  background-color: var(--aid-surface);
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  background-image: url("data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%2010%206%22%3E%3Cpath%20fill%3D%22%23475569%22%20d%3D%22M0%200l5%206%205-6z%22%2F%3E%3C%2Fsvg%3E");
  background-repeat: no-repeat;
  background-position: right 10px center;
  background-size: 10px 6px;
  color: var(--aid-text);
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  font-family: var(--aid-font);

  &:hover {
    border-color: var(--aid-border-strong);
  }

  &:focus {
    outline: none;
    border-color: var(--aid-accent);
    box-shadow: 0 0 0 2px rgba(15, 110, 99, 0.12);
  }
}

.filter-actions {
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
  padding-top: 1rem;
  border-top: 1px solid var(--aid-border);
}

/* Buttons — token system: .btn defaults to secondary, .apply-btn is primary teal. */
.btn {
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 13px;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
  font-family: var(--aid-font);
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  border: 1px solid var(--aid-border);
  background: var(--aid-surface);
  color: var(--aid-text);

  i {
    font-size: 0.8125rem;
  }
}

.reset-btn {
  background-color: var(--aid-surface);
  border: 1px solid var(--aid-border);
  color: var(--aid-text);

  &:hover {
    background-color: var(--aid-surface2);
    border-color: var(--aid-border-strong);
    color: var(--aid-text);
  }
}

.apply-btn {
  background: var(--aid-accent);
  border: 1px solid var(--aid-accent);
  color: white;
  box-shadow: none;

  &:hover {
    background: var(--aid-accent-ink);
    border-color: var(--aid-accent-ink);
  }
}

/* Conversations Table - Inside panel-card */

.table-header {
  display: flex;
  background-color: var(--aid-surface2);
  border-bottom: 1px solid var(--aid-border);
  font-weight: 600;
  font-size: 11px;
  color: var(--aid-text-mute);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.header-cell, .table-cell {
  padding: 0.75rem 1rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: flex;
  align-items: center;
}

.cell-attn {
  flex: 0 0 36px;
  justify-content: center;
  text-align: center;
}

.cell-date {
  flex: 0 0 170px;
}

.cell-mode {
  flex: 0 0 90px;
}

.cell-outcome {
  flex: 0 0 130px;
}

.cell-patient {
  flex: 1;
  min-width: 90px;
}

.cell-sentiment {
  flex: 0 0 110px;
}

.cell-cs {
  flex: 0 0 80px;
  text-align: center;
  justify-content: center;
}

.cell-duration {
  flex: 0 0 90px;
  text-align: center;
  justify-content: center;
}

.cell-cost {
  flex: 0 0 80px;
  text-align: right;
  justify-content: flex-end;
}

.cell-actions {
  flex: 0 0 120px;
  text-align: right;
  justify-content: flex-end;
}

/* Needs-attention row highlight + marker */
.table-row.row-attn {
  background-color: rgba(239, 68, 68, 0.05);
}

.table-row.row-attn:hover {
  background-color: rgba(239, 68, 68, 0.09);
}

.attn-marker {
  font-size: 0.9375rem;
  cursor: help;
}

.mode-label {
  font-weight: 500;
  color: var(--aid-text-mute);
}

/* Sentiment chip — danger=negative / neutral=grey / success=positive (pills) */
.sentiment-chip {
  display: inline-block;
  padding: 0.2rem 0.6rem;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
}

.sentiment-chip.sentiment-negative {
  background-color: var(--aid-danger-bg);
  color: var(--aid-danger-text);
}

.sentiment-chip.sentiment-neutral {
  background-color: var(--aid-surface2);
  color: var(--aid-text-mute);
}

.sentiment-chip.sentiment-positive {
  background-color: var(--aid-accent-soft);
  color: var(--aid-accent-ink);
}

.sentiment-chip.sentiment-unknown {
  background-color: transparent;
  color: var(--aid-text-dim);
  font-weight: 500;
}

.cost-footnote {
  margin: 0;
  font-size: 12px;
  color: var(--aid-text-dim);
  font-style: italic;
}

.table-row {
  display: flex;
  border-bottom: 1px solid var(--aid-border);
  cursor: pointer;
  transition: background 0.15s ease;
  font-size: 13px;
  color: var(--aid-text-mute);
  font-variant-numeric: tabular-nums;
}

.table-row:last-child {
  border-bottom: none;
}

.table-row:hover {
  background-color: var(--aid-surface2);
}

.type-indicator {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  margin-right: 0.625rem;
  font-size: 0.8125rem;
}

.type-indicator.text {
  background: var(--aid-accent-soft);
  color: var(--aid-accent);
}

.type-indicator.voice {
  background: var(--aid-surface2);
  color: var(--aid-text-mute);
}

.type-label {
  font-weight: 500;
  color: var(--aid-text-mute);
}

/* Outcome badges (pills): booked→teal/success, cancelled/transferred→danger,
   rescheduled→neutral surface, unknown→muted gray. */
.status-badge {
  display: inline-block;
  padding: 0.2rem 0.6rem;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
}

.status-badge.completed {
  background-color: var(--aid-accent-soft);
  color: var(--aid-accent-ink);
}

.status-badge.failed,
.status-badge.incomplete {
  background-color: var(--aid-danger-bg);
  color: var(--aid-danger-text);
}

.status-badge.in-progress {
  background-color: var(--aid-surface2);
  color: var(--aid-text-mute);
}

.status-badge.unknown {
  background-color: var(--aid-surface2);
  color: var(--aid-text-dim);
}

.action-btn {
  background: var(--aid-surface);
  border: 1px solid var(--aid-border);
  padding: 0.3rem 0.5rem;
  margin-left: 0.375rem;
  cursor: pointer;
  font-size: 13px;
  border-radius: 6px;
  color: var(--aid-text-mute);
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.action-btn:hover {
  background: var(--aid-surface2);
  border-color: var(--aid-border-strong);
}

.view-btn {
  padding: 0.3rem 0.7rem;
  background: var(--aid-accent-soft);
  color: var(--aid-accent-ink);
  border-color: transparent;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.025em;

  &:hover {
    background: #d9eae7;
    border-color: transparent;
  }
}

.audio-btn {
  background-color: var(--aid-surface2);
  color: var(--aid-text-mute);
  border-color: transparent;

  &:hover {
    background-color: var(--aid-border);
    border-color: transparent;
  }
}

.delete-btn {
  background-color: var(--aid-danger-bg);
  color: var(--aid-danger-text);
  border-color: transparent;

  &:hover {
    background-color: #fde2e2;
    border-color: transparent;
  }
}

/* Loading State */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 1rem;
  gap: 1.25rem;
  color: var(--aid-text-mute);
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid var(--aid-border);
  border-top-color: var(--aid-accent);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.loading-text {
  color: var(--aid-text-mute);
  font-size: 13px;
  font-weight: 500;
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 1.5rem;
  text-align: center;
  background: var(--aid-surface);
}

.empty-icon {
  font-size: 3rem;
  margin-bottom: 1.25rem;
  opacity: 0.8;
}

.empty-text {
  font-size: 14px;
  font-weight: 600;
  color: var(--aid-text);
  margin-bottom: 0.5rem;
}

.empty-subtext {
  font-size: 13px;
  color: var(--aid-text-mute);
  max-width: 320px;
  line-height: 1.5;
}

/* Pagination */
.pagination-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.25rem;
  background: var(--aid-surface);
  border-radius: 10px;
  border: 1px solid var(--aid-border);
  box-shadow: var(--aid-shadow-card);
}

.page-info {
  color: var(--aid-text-mute);
  font-size: 13px;
  font-weight: 500;

  span {
    color: var(--aid-text);
    font-weight: 600;
  }
}

.page-buttons {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.page-numbers {
  display: flex;
  gap: 0.25rem;
}

.page-btn, .page-number {
  min-width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--aid-border);
  border-radius: 8px;
  background-color: var(--aid-surface);
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  color: var(--aid-text);
  transition: background 0.15s ease, border-color 0.15s ease;
  font-family: var(--aid-font);
  padding: 0 1rem;
}

.page-btn:hover:not(:disabled) {
  background-color: var(--aid-surface2);
  border-color: var(--aid-border-strong);
}

.page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background-color: var(--aid-surface2);
}

.page-number.active {
  background: var(--aid-accent);
  color: white;
  border-color: var(--aid-accent);
  box-shadow: none;
}

.prev-btn::before {
  content: '\2190';
  margin-right: 0.375rem;
}

.next-btn::after {
  content: '\2192';
  margin-left: 0.375rem;
}

/* Modal Styles */
.delete-modal,
.audio-player-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(15, 23, 42, 0.5);
  z-index: 1040;
}

.modal-dialog {
  position: relative;
  z-index: 1050;
  width: 90%;
  max-width: 400px;
  margin: 1.75rem auto;
}

.modal-content {
  position: relative;
  background-color: var(--aid-surface);
  border-radius: 10px;
  box-shadow: 0 20px 25px -5px rgba(15, 23, 42, 0.12), 0 10px 10px -5px rgba(15, 23, 42, 0.05);
  overflow: hidden;
  border: 1px solid var(--aid-border);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid var(--aid-border);
  background-color: var(--aid-surface2);
}

.modal-title {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: var(--aid-text);
}

.modal-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  line-height: 1;
  cursor: pointer;
  color: var(--aid-text-mute);
  padding: 0;
  transition: color 0.15s ease;
}

.modal-close:hover {
  color: var(--aid-text);
}

.modal-body {
  padding: 1.5rem 1.25rem;
}

.modal-body p {
  margin: 0 0 0.5rem;
  color: var(--aid-text-mute);
  font-size: 13px;
}

.text-danger {
  color: var(--aid-danger-text);
  font-size: 13px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1rem 1.25rem;
  border-top: 1px solid var(--aid-border);
  background-color: var(--aid-surface2);
}

.btn-secondary {
  background-color: var(--aid-surface);
  border: 1px solid var(--aid-border);
  color: var(--aid-text);
}

.btn-secondary:hover {
  background-color: var(--aid-surface2);
}

.btn-danger {
  background-color: var(--aid-danger-text);
  border: 1px solid var(--aid-danger-text);
  color: white;
}

.btn-danger:hover {
  background-color: #991b1b;
}

/* Responsive Adjustments */
@media (max-width: 768px) {
  .conversation-list {
    padding: 0.75rem;
    gap: 1rem;
  }

  .filter-row {
    flex-direction: column;
    align-items: stretch;
  }

  .search-row {
    flex-direction: column;
    gap: 1rem;
  }

  .search-box {
    max-width: none;
  }

  /* Stack the filter actions full-width so the buttons are easy thumb targets. */
  .filter-actions {
    flex-direction: column-reverse;
  }
  .filter-actions .btn {
    width: 100%;
    justify-content: center;
  }

  /* Stacked-card table: hide the header row, turn each row into a card, and label
     every value with its column name via data-label (::before). Each row stays
     tappable to open the conversation. */
  .table-header {
    display: none;
  }

  .table-row {
    flex-direction: column;
    padding: 1rem 1rem 1rem;
    position: relative;
    gap: 0.15rem;
    border-bottom: 8px solid var(--aid-surface2); /* visible gap between cards */
  }

  .table-cell {
    flex: none;
    width: 100%;
    padding: 0.3rem 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    border-bottom: none;
    white-space: normal;
    overflow: visible;
    text-overflow: clip;
    text-align: right;
  }

  /* Column name on the left; value (the cell's own text) sits to the right. */
  .table-cell::before {
    content: attr(data-label);
    font-weight: 600;
    color: var(--aid-text-mute);
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    flex: 0 0 auto;
    margin-right: auto;
    text-align: left;
  }

  /* The attn cell carries an empty label and only sometimes a marker — when empty,
     drop it from the card so there's no blank labelled row. */
  .cell-attn {
    position: absolute;
    top: 0.85rem;
    right: 1rem;
    width: auto;
    padding: 0;
  }
  .cell-attn::before {
    content: none;
  }

  /* Actions row: label hidden, buttons aligned right and full-height tappable. */
  .cell-actions {
    justify-content: flex-end;
    padding-top: 0.5rem;
    margin-top: 0.25rem;
    border-top: 1px solid var(--aid-border);
  }
  .cell-actions::before {
    content: none;
  }
  .cell-actions .action-btn {
    padding: 0.5rem 0.85rem;
    min-height: 40px;
  }

  .pagination-controls {
    flex-direction: column;
    gap: 1rem;
  }

  .page-buttons {
    margin: 0 auto;
  }
  .page-btn {
    min-height: 40px;
  }
}

/* Error State */
.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
  text-align: center;
}

.error-icon {
  font-size: 2.5rem;
  margin-bottom: 1rem;
}

.error-text {
  font-size: 14px;
  font-weight: 600;
  color: var(--aid-text);
  margin-bottom: 0.5rem;
}

.error-message {
  font-size: 13px;
  color: var(--aid-danger-text);
  margin-bottom: 1rem;
  max-width: 400px;
}

.retry-btn {
  background-color: var(--aid-accent);
  color: white;
  border: 1px solid var(--aid-accent);
  padding: 6px 12px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  transition: background-color 0.15s ease;
}

.retry-btn:hover {
  background-color: var(--aid-accent-ink);
  border-color: var(--aid-accent-ink);
}

/* Audio Player Modal Specific Styles */
.audio-player-modal .modal-dialog {
  max-width: 500px;
}

.audio-player-modal .modal-body {
  text-align: center;
}

.audio-player-modal .modal-body audio {
  width: 100%;
  margin-top: 1rem;
  border-radius: 8px;
}

.audio-player-modal .loading-indicator {
  padding: 1.5rem;
  font-size: 13px;
  color: var(--aid-text-mute);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
}

.audio-player-modal .loading-indicator .spinner {
  width: 24px;
  height: 24px;
  border: 3px solid var(--aid-border);
  border-top-color: var(--aid-accent);
  border-radius: 50%;
  display: inline-block;
  animation: spin 0.8s linear infinite;
}

.audio-player-modal .error-message {
  padding: 1.5rem;
  color: var(--aid-danger-text);
}

.audio-player-modal .error-message button {
  margin-top: 1rem;
  padding: 6px 12px;
  background-color: var(--aid-accent);
  color: white;
  border: 1px solid var(--aid-accent);
  border-radius: 8px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  transition: background-color 0.15s ease;
}

.audio-player-modal .error-message button:hover {
  background-color: var(--aid-accent-ink);
  border-color: var(--aid-accent-ink);
}

/* Custom Date Input — native <input type="date"> sharing the select base; drop the
   inherited chevron SVG (it owns the native calendar glyph) and align to the token
   input look. */
.custom-date-input {
  height: calc(1.5em + 0.75rem + 2px);
  padding: 7px 10px;
  border: 1px solid var(--aid-border);
  border-radius: 6px;
  font-size: 13px;
  color: var(--aid-text);
  background-image: none;
}

.filter-item.custom-date-filter {
  margin-top: 0;
}
</style> 